import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { reportError } from "@/lib/errorReport";

interface Props {
  children: ReactNode;
  /** Optional fallback override; defaults to the branded full-screen UI. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-level error boundary. Catches any uncaught render error below it
 * and shows a branded fallback instead of a white screen. Forwards the
 * error to Sentry (when configured via the loader script) and offers a
 * "Reload" action that resets the SPA.
 *
 * Mount near the top of the React tree — see main.tsx.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      scope: "react.errorBoundary",
      level: "fatal",
      context: { componentStack: info.componentStack },
    });
    // Also log to console so devs see it locally.
    console.error("[ErrorBoundary]", error, info);
  }

  private handleReload = () => {
    // Hard reload rather than soft state reset — the safest recovery for an
    // unknown render error. Preserves auth (the session is in storage).
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const message = this.state.error?.message ?? "Something went wrong.";

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="min-h-screen flex items-center justify-center px-6 py-12"
        style={{ background: "#080808", color: "#F5F0E8" }}
      >
        <div className="max-w-md w-full text-center">
          <div
            className="mx-auto mb-6 inline-flex h-14 w-14 items-center justify-center rounded-full"
            style={{
              background: "rgba(201,168,76,0.13)",
              color: "#E8C97A",
            }}
          >
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h1
            className="mb-3 font-display text-2xl font-bold"
            style={{ fontFamily: "'Playfair Display',Georgia,serif" }}
          >
            Something went wrong
          </h1>
          <p className="mb-6 text-sm leading-relaxed" style={{ color: "#9c937f" }}>
            We hit an unexpected error and the page can't render. Our team has
            been notified. Try reloading — your session is preserved.
          </p>
          {message && (
            <p
              className="mb-6 rounded-md border px-3 py-2 text-left text-[11px] leading-snug"
              style={{
                borderColor: "rgba(201,168,76,0.18)",
                background: "rgba(255,255,255,0.02)",
                color: "#9c937f",
              }}
            >
              <span style={{ color: "#cfc7b8", fontWeight: 600 }}>Error:</span>{" "}
              {message}
            </p>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold"
            style={{
              background: "linear-gradient(135deg,#E8C97A,#C9A84C)",
              color: "#080808",
            }}
          >
            <RefreshCw className="h-4 w-4" />
            Reload Nestd
          </button>
          <p
            className="mt-6 text-[10px] uppercase tracking-[2px]"
            style={{ color: "#9c937f" }}
          >
            If this keeps happening, email support@nestd.co.za
          </p>
        </div>
      </div>
    );
  }
}
